import { ComponentType, ReactNode } from 'react';

import { ProductDocumentTypeId } from 'types/models/ProductDocumentType';
import Button from '@components/atoms/Button';
import Modal from '@components/Modal';
import StpFilePreview from '@components/StpFilePreview';
import PdfFilePreview from '@components/PdfFilePreview';
import GltfFilePreview from '@components/GltfFilePreview';
import { Props as FileViewerProps } from '@components/FilePreview';
import { FormattedMessage } from 'utils/intl';
import useBoolean from 'hooks/useBoolean';

import classes from './FilePreviewModal.module.scss';
import messages from './FilePreviewModal.messages';

type Props = {
  fileType: ProductDocumentTypeId;
  fileName: string;
  fileUrl: string;
  downloadFileName: string;
  children: ({ onClick }: { onClick: () => void }) => ReactNode;
  onClose?: () => void;
};

const componentMap: Record<ProductDocumentTypeId, ComponentType<Omit<FileViewerProps, 'fileType' | 'preview'>>> = {
  PDF: PdfFilePreview,
  GLTF: GltfFilePreview,
  STP: StpFilePreview,
};

const FilePreviewModal = ({ fileName, fileUrl, fileType, downloadFileName, children, onClose }: Props): JSX.Element => {
  const [isModalOpened, { setValue: setModalState }] = useBoolean(false);
  const Component = componentMap[fileType];

  const onClick = () => {
    setModalState(true);
  };

  const onModalStateChange = (value: boolean) => {
    setModalState(value);

    if (isModalOpened && !value && onClose) {
      onClose();
    }
  };

  const onCloseClick = () => {
    setModalState(false);

    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {children({ onClick })}
      <Modal
        size={Modal.size.big}
        controlledIsOpen={isModalOpened}
        onOpenChange={onModalStateChange}
        hideWhenClickOutside
      >
        <Component
          fileName={fileName}
          fileUrl={fileUrl}
          downloadFileName={downloadFileName}
          classes={{ root: classes.preview, object: classes.object, embed: classes.embed }}
          additionalButton={({ classes: buttonClasses }) => (
            <Button
              color="quaternary"
              variant="outlined"
              shape="round"
              size="lg"
              classes={buttonClasses}
              onClick={onCloseClick}
            >
              <FormattedMessage {...messages.cancel} />
            </Button>
          )}
          onClose={onCloseClick}
        />
      </Modal>
    </>
  );
};

export default FilePreviewModal;
